import { useState, useEffect } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate required fields
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const message = formData.get('message') as string;
    
    if (!name || !email || !message) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (message.length > 500) {
      alert('Message must be 500 characters or less');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d6jf3rfrgrhbthj8mu6g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        alert('Thank you for your message! We will get back to you soon.');
        form.reset();
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      alert('Failed to send message. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://public.readdy.ai/ai/img_res/35a93dc4-5ea1-469e-81e4-6eabc6a14a3b.png" alt="Tøft AS Logo" className="w-10 h-10 object-contain" />
            <span className="text-xl font-medium text-gray-900">Tøft AS</span>
          </div>
          <div className="hidden md:flex items-center gap-10">
            <a href="#home" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Home</a>
            <a href="#about" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">About</a>
            <a href="#products" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Products</a>
            <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Contact</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white pt-24 pb-20 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-16 items-center relative z-10">
          <div className="text-center lg:text-left">
            <h1 className="text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6">
              Modern iOS<br />
              Applications<br />
              <span className="text-indigo-600">for Your Security</span>
            </h1>
            <p className="text-xl text-gray-600 font-light max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Expert team developing VPN and Cleaner solutions to enhance your mobile experience
            </p>
          </div>
          <div className="flex justify-center lg:justify-end">
            <div className="w-full h-full max-w-md">
              <img 
                src="https://public.readdy.ai/ai/img_res/152975197eb09becff2dc2169d9f334a.jpg" 
                alt="VPN App Interface" 
                className="w-full h-auto object-contain drop-shadow-2xl transform hover:scale-105 transition-transform duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-gradient-to-r from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-2 h-2 bg-gray-900 rounded-full"></div>
              <span className="text-sm font-semibold text-gray-900 uppercase tracking-wide">About Tøft AS</span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-light text-gray-900 leading-tight mb-8">
              Expert Team<br />
              Building Modern<br />
              <span className="font-semibold">iOS Solutions</span>
            </h2>
          </div>
          <div className="space-y-6">
            <p className="text-lg text-gray-700 leading-relaxed">
              We are a dedicated team of iOS developers and designers, passionate about creating applications that make a difference in users' daily lives. Our expertise lies in developing cutting-edge mobile solutions that prioritize user experience and security.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our focus on VPN and Cleaner technologies stems from our commitment to user privacy and device performance. We believe that every iOS user deserves applications that are both powerful and intuitive, protecting their digital life while optimizing their device capabilities.
            </p>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-1 h-1 bg-indigo-600 rounded-full"></div>
              <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Our Products</span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              VPN & Cleaner<br />
              Solutions
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-20">
            {/* VPN Card */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-5xl text-indigo-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">VPN Protection</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Secure your connection with military-grade encryption. Browse privately and access content from anywhere in the world with lightning-fast speeds. Our VPN ensures your online activities remain confidential and protected from prying eyes.
              </p>
            </div>

            {/* Cleaner Card */}
            <div className="bg-white rounded-3xl p-12 shadow-sm hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 flex items-center justify-center mb-8">
                <i className="ri-brush-line text-5xl text-emerald-500"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Device Cleaner</h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Optimize your iPhone performance by removing junk files, managing storage, and boosting battery life with intelligent cleaning algorithms. Keep your device running smoothly and efficiently with our advanced optimization tools.
              </p>
            </div>
          </div>

          {/* Product Showcase */}
          <div className="mb-12">
            <h3 className="text-4xl font-bold text-gray-900 text-center mb-16">Featured Applications</h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* VPN Pro */}
            <div className="bg-gray-100 rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
              <div className="relative p-6">
                <span className="absolute top-6 left-6 bg-gray-900 text-white text-xs font-semibold px-4 py-2 rounded-xl">Premium</span>
                <div className="w-full h-80 flex items-center justify-center mt-8">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20showing%20premium%20VPN%20application%20interface%20with%20world%20map%20and%20server%20locations%2C%20dark%20blue%20gradient%20background%2C%20connection%20button%20and%20speed%20indicator%2C%20professional%20app%20screenshot%20with%20clean%20minimal%20design%2C%20simple%20light%20background&width=280&height=320&seq=vpn-pro-app-001&orientation=portrait" 
                    alt="VPN Pro" 
                    className="w-48 h-auto object-contain"
                  />
                </div>
              </div>
              <div className="p-6 text-center">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">VPN Pro</h4>
                <p className="text-sm text-gray-600">Premium Security</p>
              </div>
            </div>

            {/* Cleaner */}
            <div className="bg-gray-100 rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
              <div className="relative p-6">
                <div className="w-full h-80 flex items-center justify-center mt-8">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20displaying%20device%20cleaner%20application%20with%20storage%20analysis%20circular%20charts%20and%20clean%20button%2C%20emerald%20green%20gradient%20interface%2C%20file%20categories%20and%20optimization%20options%2C%20modern%20iOS%20app%20design%20screenshot%2C%20simple%20light%20background&width=280&height=320&seq=cleaner-app-002&orientation=portrait" 
                    alt="Cleaner" 
                    className="w-48 h-auto object-contain"
                  />
                </div>
              </div>
              <div className="p-6 text-center">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Cleaner</h4>
                <p className="text-sm text-gray-600">Smart Optimization</p>
              </div>
            </div>

            {/* VPN Basic */}
            <div className="bg-gray-100 rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
              <div className="relative p-6">
                <div className="w-full h-80 flex items-center justify-center mt-8">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20with%20basic%20VPN%20application%20showing%20simple%20connect%20interface%20with%20large%20power%20button%2C%20light%20blue%20gradient%20background%2C%20connection%20status%20and%20location%20display%2C%20minimalist%20app%20screenshot%20design%2C%20simple%20light%20background&width=280&height=320&seq=vpn-basic-app-003&orientation=portrait" 
                    alt="VPN Basic" 
                    className="w-48 h-auto object-contain"
                  />
                </div>
              </div>
              <div className="p-6 text-center">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">VPN Basic</h4>
                <p className="text-sm text-gray-600">Essential Protection</p>
              </div>
            </div>

            {/* Cleaner Pro */}
            <div className="bg-gray-100 rounded-2xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300">
              <div className="relative p-6">
                <div className="w-full h-80 flex items-center justify-center mt-8">
                  <img 
                    src="https://readdy.ai/api/search-image?query=iPhone%20mockup%20showing%20advanced%20cleaner%20pro%20application%20with%20detailed%20storage%20breakdown%20graphs%2C%20battery%20optimization%20features%2C%20deep%20green%20gradient%20interface%2C%20premium%20cleaning%20tools%20and%20analytics%2C%20professional%20app%20screenshot%2C%20simple%20light%20background&width=280&height=320&seq=cleaner-pro-app-004&orientation=portrait" 
                    alt="Cleaner Pro" 
                    className="w-48 h-auto object-contain"
                  />
                </div>
              </div>
              <div className="p-6 text-center">
                <h4 className="text-xl font-semibold text-gray-900 mb-2">Cleaner Pro</h4>
                <p className="text-sm text-gray-600">Advanced Tools</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 px-6 bg-gradient-to-br from-blue-900 to-blue-600 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.1) 50px, rgba(255,255,255,0.1) 51px)', backgroundSize: '100% 100%' }}></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-12">
            <h2 className="text-5xl lg:text-7xl font-bold text-white leading-tight max-w-2xl">
              ready to enhance<br />your iOS experience
            </h2>
            <p className="text-xl text-white/90 font-light max-w-lg leading-relaxed lg:text-right">
              Join thousands of users who trust our applications for their daily security and performance needs
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Left Column - Company Info */}
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-8">Get in Touch</h2>
              <p className="text-lg text-gray-600 mb-12 leading-relaxed">
                Have questions about our applications? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center mt-1">
                    <i className="ri-map-pin-line text-2xl text-indigo-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Address</p>
                    <p className="text-gray-600">c/o Frank Arild Hagen Tomtegata 5C</p>
                    <p className="text-gray-600">LILLESTROM, 2004</p>
                    <p className="text-gray-600">Norway</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center mt-1">
                    <i className="ri-mail-line text-2xl text-indigo-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Email</p>
                    <a href="mailto:info@toft-as.com" className="text-gray-600 hover:text-indigo-600 transition-colors cursor-pointer">info@toft-as.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center mt-1">
                    <i className="ri-phone-line text-2xl text-indigo-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Phone</p>
                    <a href="tel:+15078892390" className="text-gray-600 hover:text-indigo-600 transition-colors cursor-pointer">+1 507 889 2390</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="bg-white rounded-3xl p-10 shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h3>
              <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent outline-none transition-all"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent outline-none transition-all"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <input 
                    type="text" 
                    id="subject" 
                    name="subject" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent outline-none transition-all"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    required 
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-600 focus:border-transparent outline-none transition-all resize-none"
                    placeholder="Your message (max 500 characters)"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-indigo-600 text-white font-semibold py-4 px-6 rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-50 py-20 px-6 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            {/* Left Column - Company Info */}
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/35a93dc4-5ea1-469e-81e4-6eabc6a14a3b.png" alt="Tøft AS Logo" className="w-10 h-10 object-contain" />
                <h3 className="text-3xl font-bold text-gray-900">Tøft AS</h3>
              </div>
              <p className="text-base text-gray-600 leading-relaxed mb-12">
                Developing innovative iOS applications since 2020. Committed to user privacy and exceptional performance.
              </p>
            </div>

            {/* Right Columns - Navigation */}
            <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">Products</h4>
                <ul className="space-y-3">
                  <li><span className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">VPN Pro</span></li>
                  <li><span className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">VPN Basic</span></li>
                  <li><span className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Cleaner</span></li>
                  <li><span className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Cleaner Pro</span></li>
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">Company</h4>
                <ul className="space-y-3">
                  <li><a href="#about" className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">About Us</a></li>
                  <li><span className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Our Team</span></li>
                  <li><span className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Careers</span></li>
                  <li><span className="text-sm text-gray-600 hover:text-gray-900 transition-colors cursor-pointer">Press Kit</span></li>
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">Contact</h4>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li>
                    <span className="font-medium text-gray-700">Address:</span><br />
                    c/o Frank Arild Hagen<br />
                    Tomtegata 5C
                  </li>
                  <li>
                    <span className="font-medium text-gray-700">City:</span> LILLESTROM
                  </li>
                  <li>
                    <span className="font-medium text-gray-700">Zip:</span> 2004
                  </li>
                  <li>
                    <span className="font-medium text-gray-700">Country:</span> Norway
                  </li>
                  <li>
                    <span className="font-medium text-gray-700">Email:</span><br />
                    <a href="mailto:info@toft-as.com" className="hover:text-indigo-600 transition-colors cursor-pointer">info@toft-as.com</a>
                  </li>
                  <li>
                    <span className="font-medium text-gray-700">Phone:</span><br />
                    <a href="tel:+15078892390" className="hover:text-indigo-600 transition-colors cursor-pointer">+1 507 889 2390</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">© 2025 Tøft AS. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}